import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { redirect } from "react-router";
import { calculateMaxAge } from "@/common/utils/calculate-max-age";
import { decodeJwt } from "@/common/utils/jwt";
import { SessionAuthCookies } from "../cookies";
import { env } from "../env";

export const httpClient = axios.create({
  baseURL: env.VITE_BASE_API_URL,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
}

function clearAuthData() {
  SessionAuthCookies.remove();
  localStorage.removeItem("refresh_token");
  httpClient.defaults.headers.common.Authorization = undefined;
  window.dispatchEvent(new CustomEvent("auth:unauthorized"));
}

/**
 * Strip empty-string values from an object; leave `null` alone.
 *
 * `null` is this app's app-wide "clear this field" signal for every
 * `nullable().optional()` API schema field (D20), a pattern that now covers
 * 9 domains (`tags`, `competitions`, `clubs`, `participants`, `players`,
 * `seasons`, `teams`, `role-types`, `sports`). An *omitted* key, by
 * contrast, means "leave unchanged" server-side (`!== undefined` gates,
 * e.g. `TagService.updateTag`). Stripping `null` here used to silently
 * convert "clear" into "leave unchanged" for every one of those fields —
 * see DEF-17/D145. `undefined`-valued keys need no explicit handling here:
 * axios's serializer already omits them via `JSON.stringify` before the
 * request is sent.
 */
function stripEmptyStringValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "")) as Partial<T>;
}

/**
 * Request interceptor `onFulfilled`: attaches the `Authorization` header
 * from the session cookie (if present) and sanitizes JSON request bodies
 * via `stripEmptyStringValues`, skipping `FormData` bodies. Extracted to a
 * named, exported function (rather than an inline arrow) so tests can
 * invoke the exact function object that ships to production without
 * making a real HTTP request.
 */
export function attachAuthAndSanitizePayload(config: InternalAxiosRequestConfig) {
  const token = SessionAuthCookies.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Strip empty-string values from request body for JSON payloads
  if (config.data && typeof config.data === "object" && !(config.data instanceof FormData)) {
    config.data = stripEmptyStringValues(config.data as Record<string, unknown>);
  }

  return config;
}

httpClient.interceptors.request.use(
  attachAuthAndSanitizePayload,
  (error) => Promise.reject(error instanceof Error ? error : new Error(String(error)))
);

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (originalRequest.url?.includes("/auth/refresh")) {
      clearAuthData();
      redirect(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return httpClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const storedRefreshToken = localStorage.getItem("refresh_token");
    if (!storedRefreshToken) {
      isRefreshing = false;
      clearAuthData();
      redirect(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post<{ data: { accessToken: string } }>(
        `${env.VITE_BASE_API_URL}/auth/refresh`,
        {
          refreshToken: storedRefreshToken,
        }
      );

      const newAccessToken: string = data.data.accessToken;
      const decoded = decodeJwt<{ exp?: number }>(newAccessToken);
      const maxAge = calculateMaxAge(decoded.exp);

      SessionAuthCookies.set(newAccessToken, { maxAge });
      httpClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

      window.dispatchEvent(
        new CustomEvent("auth:token-refreshed", { detail: { accessToken: newAccessToken } })
      );

      processQueue(null, newAccessToken);
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return httpClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearAuthData();
      redirect(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return Promise.reject(
        refreshError instanceof Error ? refreshError : new Error(String(refreshError))
      );
    } finally {
      isRefreshing = false;
    }
  }
);
